export const APP_VERSION = "0.1.0";
export const SCHEMA_VERSION = "0.2.0";
export const RULES_VERSION = "0.2.0";

export type TriState = "yes" | "no" | "unknown";
export type TrafficLight = "green" | "yellow" | "red";
export type ModuleState = "not_started" | "in_progress" | "complete" | "needs_review";
export type ActionStatus = "open" | "in_progress" | "done" | "needs_external_review" | "postponed";

export interface BaseRecord {
  id: string;
  schema_version: string;
  created_at: string;
  updated_at: string;
}

export interface BusinessProfile extends BaseRecord {
  business_name?: string;
  owner_name?: string;
  documentation_recipient?: string;
  business_location?: string;
  business_segment: string;
  business_segment_custom?: string;
  service_areas: string[];
  service_area_custom?: string;
  customer_types: string[];
  customer_type_custom?: string;
  ai_usage_level: string;
  ai_usage_custom?: string;
  handles_sensitive_contexts: TriState;
  preliminary_role_profile?: "deployer" | "provider_non_high_risk" | "provider_high_risk";
  preliminary_role_confidence?: {
    deployer: number;
    provider: number;
  };
  preliminary_role_answers?: Record<string, TriState>;
  onboarding_completed: boolean;
  local_first_acknowledged: boolean;
}

export interface AITool extends BaseRecord {
  name?: string;
  category: string;
  status: "active" | "planned" | "tested" | "paused" | "archived" | "needs_review";
  usage_contexts: string[];
  data_categories: string[];
  human_review: string;
  notes?: string;
}

export interface AIUseCase extends BaseRecord {
  title?: string;
  category: string;
  context: string;
  tool_category: string;
  tool_ids?: string[];
  data_categories: string[];
  output_type: string;
  human_review: string;
  customer_interaction: TriState;
  automated_decision_about_people: TriState;
  description?: string;
  status: "draft" | "complete" | "needs_review" | "archived";
}

export interface RiskAssessment extends BaseRecord {
  source_type: "tool" | "use_case";
  source_id: string;
  traffic_light: TrafficLight;
  reasons: string[];
  positive_factors: string[];
  red_review_points: string[];
}

export interface ActionItem extends BaseRecord {
  source_type: string;
  source_id?: string;
  title: string;
  category: string;
  priority: "low" | "medium" | "high";
  status: ActionStatus;
  completion_note?: string;
  completed_at?: string;
}

export interface RoleCheckRecord extends BaseRecord {
  profile: "A" | "B" | "C" | "D";
  signals: string[];
  requires_external_review: boolean;
  ai_act_role_profile?: "deployer" | "provider_non_high_risk" | "provider_high_risk";
  ai_act_role_confidence?: {
    deployer: number;
    provider: number;
  };
  ai_act_role_finalized?: boolean;
}

export interface LiteracyRecord extends BaseRecord {
  topics_completed: string[];
  quiz_score: number;
  passed: boolean;
  self_confirmed: boolean;
  answers?: Record<string, string>;
  completed_at?: string;
  evidence_markdown?: string;
}

export interface TransparencyCheck extends BaseRecord {
  customer_facing: TriState;
  public_output: TriState;
  direct_ai_interaction: TriState;
  status: "none_needed" | "notice_recommended" | "external_review_recommended";
  recommended_notices: string[];
}

export interface GeneratedDocument extends BaseRecord {
  type: string;
  source_id?: string;
  title: string;
  content_markdown: string;
  status: "draft" | "ready_to_export" | "exported" | "needs_update";
}

export interface ModuleStatus extends BaseRecord {
  module: string;
  state: ModuleState;
  summary: string;
}

export interface ReadinessScore extends BaseRecord {
  score: number;
  explanation: string;
  red_points_count: number;
}

export interface PrivacyGateEvent extends BaseRecord {
  task_type: AITaskType;
  sent_fields: string[];
  blocked_fields: string[];
  confirmed: boolean;
}

export interface AIAssistUsage extends BaseRecord {
  task_type: AITaskType;
  module: string;
  credits_used: number;
  success: boolean;
}

export interface AICreditLedger extends BaseRecord {
  delta: number;
  reason: string;
}

export interface LicenseActivationCache extends BaseRecord {
  activationId: string;
  activationToken: string;
  licenseFingerprint: string;
  keySuffix: string;
  validatedAt: string;
  gracePeriodDays: number;
}

export interface AppSettings extends BaseRecord {
  credits_remaining: number;
  storage_mode: "sqlite-opfs" | "sqlite-wasm-fallback" | "indexeddb-fallback";
  ai_assist_enabled_by_user?: boolean;
  last_backup_at?: string;
}

export interface ExportHistory extends BaseRecord {
  export_type: "json" | "csv" | "markdown" | "pdf";
  filename: string;
}

export interface ImportHistory extends BaseRecord {
  filename: string;
  imported_at: string;
}

export type AITaskType =
  | "use_case_summary"
  | "use_case_policy_clause"
  | "use_case_customer_notice"
  | "use_case_dossier_section"
  | "tool_rule_text"
  | "tool_dossier_section"
  | "tool_action_hint"
  | "role_check_summary"
  | "role_check_action_explanation"
  | "role_check_dossier_section"
  | "risk_summary"
  | "risk_action_prioritization"
  | "risk_dossier_section"
  | "literacy_dossier_text"
  | "literacy_basic_rules"
  | "literacy_short_briefing"
  | "transparency_website_notice"
  | "transparency_offer_notice"
  | "transparency_customer_project_notice"
  | "transparency_content_notice"
  | "transparency_chatbot_notice"
  | "transparency_dossier_section"
  | "document_executive_summary"
  | "document_policy_text"
  | "document_customer_notice"
  | "document_action_plan_summary"
  | "document_one_page_summary"
  | "dashboard_readiness_summary"
  | "dashboard_next_steps"
  | "dashboard_recheck_notice";

export interface Workspace {
  app_version: string;
  schema_version: string;
  rules_version: string;
  exported_at: string;
  businessProfile: BusinessProfile[];
  aiTools: AITool[];
  aiUseCases: AIUseCase[];
  riskAssessments: RiskAssessment[];
  actionItems: ActionItem[];
  roleCheckRecords: RoleCheckRecord[];
  literacyRecords: LiteracyRecord[];
  transparencyChecks: TransparencyCheck[];
  generatedDocuments: GeneratedDocument[];
  moduleStatuses: ModuleStatus[];
  readinessScores: ReadinessScore[];
  privacyGateEvents: PrivacyGateEvent[];
  aiAssistUsage: AIAssistUsage[];
  exportHistory: ExportHistory[];
  importHistory: ImportHistory[];
  aiCreditLedger: AICreditLedger[];
  appSettings: AppSettings[];
}
